import React, { useState, useEffect, Fragment, useRef } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import { useAuth } from "../contexts/AuthContext";
import useImpersonationGuard from "../hooks/useImpersonationGuard";
import apiService from "../services/api";

import MarkdownEditor from "./MarkdownEditor";

const NewTicketModal = ({
  isOpen,
  onClose,
  onTicketCreated,
  initialSubject = "",
  prefilledCustomer = null,
}) => {
  const { user } = useAuth();
  const { guardImpersonatedAction, isImpersonating } = useImpersonationGuard();
  const customerDropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const appliedInitialSubjectRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTimer, setSearchTimer] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const initialCustomerId = prefilledCustomer?.id
    ? prefilledCustomer.id
    : user?.role === "user"
      ? user.id
      : "";
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    category: "general",
    priority: "medium",
    customer: initialCustomerId,
    attachments: [],
  });

  useEffect(() => {
    // When modal opens with a prefilled customer, lock formData to it.
    // When it closes, clear any free-form search state.
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        ...(initialSubject ? { subject: initialSubject } : {}),
        ...(prefilledCustomer?.id ? { customer: prefilledCustomer.id } : {}),
      }));
      if (initialSubject) {
        appliedInitialSubjectRef.current = true;
      }
    }
    if (!isOpen) {
      setCustomerSearch("");
      setSelectedCustomer(null);
      setCustomers([]);
      setShowCustomerDropdown(false);
      if (appliedInitialSubjectRef.current) {
        setFormData((prev) => ({ ...prev, subject: "" }));
        appliedInitialSubjectRef.current = false;
      }
    }
  }, [initialSubject, isOpen, prefilledCustomer?.id]);

  useEffect(
    () =>
      // Cleanup timer on unmount
      () => {
        if (searchTimer) {
          clearTimeout(searchTimer);
        }
      },
    [searchTimer]
  );

  useEffect(() => {
    // Handle click outside to close dropdown
    const handleClickOutside = (event) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchCustomers = async (searchQuery = "") => {
    try {
      const response = await apiService.searchCustomers(searchQuery);

      if (response && response.data?.users) {
        setCustomers(response.data.users);
        setShowCustomerDropdown(true);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      toast.error("Failed to load customers");
      setCustomers([]);
    }
  };

  const handleCustomerSearch = (value) => {
    setCustomerSearch(value);
    setSelectedCustomer(null);

    // Clear existing timer
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    // Set new timer for debounced search
    const timer = setTimeout(() => {
      fetchCustomers(value);
    }, 300);

    setSearchTimer(timer);
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(`${customer.name} (${customer.email})`);
    setShowCustomerDropdown(false);
    setFormData((prev) => ({ ...prev, customer: customer.id }));
  };

  const handleFileSelect = (e) => {
    if (guardImpersonatedAction("Attaching files to tickets")) {
      e.target.value = "";

      return;
    }

    const files = Array.from(e.target.files);

    setSelectedFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (guardImpersonatedAction("Creating tickets")) return;

    if (!formData.subject.trim()) {
      toast.error("Please enter a subject");

      return;
    }

    if (!formData.description.trim()) {
      toast.error("Please enter a description");

      return;
    }

    // Only require customer selection if user is admin AND no prefilled
    // customer was provided (e.g., when launched from a customer-scoped page).
    const isAdmin = user?.role === "admin";

    if (isAdmin && !prefilledCustomer && !selectedCustomer) {
      toast.error("Please select a customer");

      return;
    }

    setLoading(true);

    try {
      // Upload files first if any selected
      const uploadedAttachments = [];

      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          try {
            const response = await apiService.uploadFile(file);

            if (response.data?.files && response.data.files[0]) {
              uploadedAttachments.push(response.data.files[0]);
            }
          } catch (uploadError) {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }

      // Prepare the data for API
      const ticketData = {
        subject: formData.subject,
        description: formData.description,
        category: formData.category.replace("-", "_"), // Convert feature-request to feature_request
        priority: formData.priority,
      };

      // If admin is creating on behalf of customer
      const isAdmin = user?.role === "admin";

      if (isAdmin && formData.customer) {
        ticketData.customer = formData.customer;
      }

      // Add uploaded attachments
      if (uploadedAttachments.length > 0) {
        ticketData.attachments = uploadedAttachments;
      }

      const result = await apiService.createTicket(ticketData);

      toast.success(`Ticket ${result.ticket_number} created successfully!`);
      onTicketCreated(result);
      onClose();
      resetForm();
    } catch (error) {
      // Extract detailed error messages from API response
      let errorMessage = "Failed to create ticket";

      if (error.response?.data) {
        const { error: errorText, details } = error.response.data;

        if (details && Array.isArray(details) && details.length > 0) {
          // Display all validation errors
          errorMessage = details.join(". ");
        } else if (errorText) {
          // Display the main error message
          errorMessage = errorText;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subject: "",
      description: "",
      category: "general",
      priority: "medium",
      customer: prefilledCustomer?.id
        ? prefilledCustomer.id
        : user?.role === "user"
          ? user.id
          : "",
      attachments: [],
    });
    setCustomerSearch("");
    setSelectedCustomer(null);
    setCustomers([]);
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Transition appear as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-[70]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="div"
                  className="flex items-center justify-between mb-6"
                >
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                    Create New Ticket
                  </h3>
                  <button
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                <form className="space-y-4" onSubmit={handleSubmit}>
                  {isImpersonating && (
                    <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                      <i className="fas fa-user-shield mt-0.5" />
                      <div>
                        <p className="font-semibold">
                          Ticket creation is locked during impersonation.
                        </p>
                        <p>
                          Return to Admin before creating support tickets for a
                          customer.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Prefilled customer (locked when launched from a customer-scoped page) */}
                  {user?.role === "admin" && prefilledCustomer && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Customer
                      </label>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                          {(
                            prefilledCustomer.name ||
                            prefilledCustomer.email ||
                            "?"
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {prefilledCustomer.name || "Unknown Customer"}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {prefilledCustomer.email || "No email on file"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Customer Selection (admin only, when not prefilled) */}
                  {user?.role === "admin" && !prefilledCustomer && (
                    <div className="relative" ref={customerDropdownRef}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Customer <span className="text-red-500">*</span>
                      </label>
                      <input
                        className="input w-full"
                        placeholder="Search by name or email..."
                        required={!selectedCustomer}
                        type="text"
                        value={customerSearch}
                        onChange={(e) => handleCustomerSearch(e.target.value)}
                        onFocus={() => fetchCustomers(customerSearch)}
                      />

                      {/* Search Results Dropdown */}
                      {showCustomerDropdown && customers.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto">
                          {customers.map((customer) => (
                            <button
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none"
                              key={customer.id}
                              type="button"
                              onClick={() => selectCustomer(customer)}
                            >
                              <div className="flex items-center gap-3">
                                {customer.avatarUrl ? (
                                  <img
                                    alt={customer.name || customer.email}
                                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                    src={customer.avatarUrl}
                                  />
                                ) : (
                                  <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                                    {(customer.name || customer.email)
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>
                                )}
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {customer.name}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {customer.email}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  {customer.type === "admin" ? "Admin" : "User"}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* No Results Message */}
                      {showCustomerDropdown &&
                        customers.length === 0 &&
                        customerSearch.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                            No customers found
                          </div>
                        )}

                      {/* Selected Customer Display */}
                      {selectedCustomer && (
                        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md flex items-center justify-between">
                          <span className="text-sm text-blue-700 dark:text-blue-300">
                            Selected: {selectedCustomer.name} (
                            {selectedCustomer.email})
                          </span>
                          <button
                            className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                            type="button"
                            onClick={() => {
                              setSelectedCustomer(null);
                              setCustomerSearch("");
                              setFormData((prev) => ({
                                ...prev,
                                customer: "",
                              }));
                            }}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      className="input w-full"
                      name="subject"
                      placeholder="Brief description of the issue"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Category and Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        className="input w-full"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                      >
                        <option value="general">General</option>
                        <option value="technical">Technical</option>
                        <option value="billing">Billing</option>
                        <option value="feature_request">Feature Request</option>
                        <option value="bug">Bug</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Priority
                      </label>
                      <select
                        className="input w-full"
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <MarkdownEditor
                      disabled={isImpersonating}
                      minHeightClass="min-h-[160px]"
                      placeholder="Please provide detailed information about the issue... (Markdown formatting supported)"
                      value={formData.description}
                      onChange={(next) =>
                        setFormData((prev) => ({ ...prev, description: next }))
                      }
                    />
                  </div>

                  {/* File Attachments */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Attachments
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        multiple
                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt"
                        className="hidden"
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                      />
                      <button
                        className="btn btn-secondary"
                        disabled={isImpersonating}
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <i className="fas fa-paperclip" />
                        Choose Files
                      </button>
                      {selectedFiles.length > 0 && (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {selectedFiles.length} file(s) selected
                        </span>
                      )}
                    </div>
                    {selectedFiles.length > 0 && (
                      <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <ul className="text-sm space-y-1">
                          {selectedFiles.map((file, index) => (
                            <li className="flex items-center gap-2" key={index}>
                              <i className="fas fa-file text-gray-500" />
                              <span>{file.name}</span>
                              <span className="text-gray-500">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </li>
                          ))}
                        </ul>
                        <button
                          className="mt-2 text-red-500 hover:text-red-700 text-sm"
                          type="button"
                          onClick={() => {
                            setSelectedFiles([]);
                            if (fileInputRef.current)
                              fileInputRef.current.value = "";
                          }}
                        >
                          Clear all
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Form Actions */}
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      className="btn btn-ghost"
                      disabled={loading}
                      type="button"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      disabled={loading || isImpersonating}
                      type="submit"
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              fill="currentColor"
                            />
                          </svg>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus mr-2" />
                          Create Ticket
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default NewTicketModal;
